import { __awaiter } from "tslib";
import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { ClockContainer } from '../clockContainer';
import { OverlayContainer } from '../overlayContainer';
import { DelayedSummary } from '../delayedSummaryContainer';
import { AdFileSize } from '@ligr/api-v2';
import { Ads } from '../adsContainer';
import { createContainer } from 'unstated-next';
import { breakFactSequenceTriggerMap, liveFactSequenceTriggerMap, liveTriggers, breakTriggers, createBreakTimeline, createLiveTimeline } from './sports/sequences';
import { getCurrentPeriod } from '@ligr/shared';
import { DesiredGfxState } from '../useGfxState';
import { useAdLoop } from './useAdLoop';
import camelCase from 'camelcase';
import { detachedAd } from '../../components/DetatchedAd';
// maybe we can make this use the scoreboard also
const adLoopSequence = args => {
    const graphicBase = Object.entries(args.desiredState).find(([graphicName, { adLoop }]) => adLoop);
    if (!graphicBase) {
        return [];
    }
    return [
        {
            priority: 5,
            name: 'Sponsor Loop',
            updateType: 'update',
            transitions: [
                [
                    graphicBase[0],
                    0,
                    Object.assign(Object.assign({ updateType: 'update', adLoop: true }, graphicBase[1]), { loopConfig: graphicBase[1].adLoopConfig, adSizes: graphicBase[1].adLoopSizes, adLoopSizes: graphicBase[1].adLoopSizes })
                ],
                [graphicBase[0], args.duration, Object.assign({}, graphicBase[1])]
            ]
        }
    ];
};
const getActionProps = (action, actionProps = {}, currentPeriod, sequenceQueue, desiredState, enableAds, getNextAd, graphics) => {
    let newState;
    switch (action) {
        case 'hideAll':
            return {};
        case 'remove':
            newState = Object.assign({}, desiredState);
            actionProps === null || actionProps === void 0 ? void 0 : actionProps.toRemove.forEach((graphicName) => {
                delete newState[graphicName];
            });
            return newState;
        case 'removeAllExcept':
            newState = Object.assign({}, desiredState);
            Object.keys(newState).map(graphicName => {
                var _a;
                if (!((_a = actionProps === null || actionProps === void 0 ? void 0 : actionProps.toKeep) === null || _a === void 0 ? void 0 : _a.map(({ name }) => name).includes(graphicName))) {
                    delete newState[graphicName];
                }
            });
            actionProps === null || actionProps === void 0 ? void 0 : actionProps.toKeep.forEach(({ name, graphicProps }) => {
                var _a, _b;
                if (newState[name]) {
                    const themeGraphicSizes = ((_a = graphics.find(({ name: graphicName }) => camelCase(graphicName) === name)) === null || _a === void 0 ? void 0 : _a.imageAdSizes) || [];
                    newState[name] = Object.assign(Object.assign({}, graphicProps), (graphicProps && enableAds
                        ? getNextAd(Object.assign(Object.assign({}, graphicProps), { adSizes: (_b = graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.adSizes) === null || _b === void 0 ? void 0 : _b.filter((size) => themeGraphicSizes.includes(size)) }))
                        : undefined));
                }
            });
            return newState;
        case 'default':
            if ((sequenceQueue.length && sequenceQueue[sequenceQueue.length - 1].transitions[0][2].updateType === 'update') ||
                currentPeriod.status === 'break') {
                return {};
            }
            return {
                scoreboard: {
                    preset: 'default',
                    adLoop: true,
                    liveLoop: true,
                    adLoopSizes: [AdFileSize.small, AdFileSize.medium, AdFileSize.banner, AdFileSize.square].filter(size => { var _a; return (_a = graphics.find(({ name }) => camelCase(name) === 'scoreboard')) === null || _a === void 0 ? void 0 : _a.imageAdSizes.find(adSize => adSize === size); })
                }
            };
    }
};
const useAutomation = () => {
    var _a, _b, _c;
    const overlayContainer = OverlayContainer.useContainer();
    // Won't be rendered before this is loaded
    const match = overlayContainer.match;
    const overlay = overlayContainer.overlay;
    const { state, dispatch } = DesiredGfxState.useContainer();
    const timeoutFunction = useRef(null);
    const { desiredState, 
    // adLoop,s
    currentSequence, sequenceQueue, archivedSequences, usedFacts } = state;
    const { data: { matchSummary, newFacts } } = DelayedSummary.useContainer();
    const { clockData } = ClockContainer.useContainer();
    const [theme, setTheme] = useState();
    const loadTheme = (sport, themeName = 'default') => __awaiter(void 0, void 0, void 0, function* () {
        const { loadTheme } = (yield import(`@ligr/themes/lib/${camelCase(sport)}/index.js`)).default;
        const theme = (yield loadTheme(themeName)).default;
        setTheme(theme);
    });
    useEffect(() => {
        var _a;
        if ((_a = match === null || match === void 0 ? void 0 : match.competition) === null || _a === void 0 ? void 0 : _a.sport.name) {
            loadTheme(match.competition.sport.name);
        }
    }, [(_a = match === null || match === void 0 ? void 0 : match.competition) === null || _a === void 0 ? void 0 : _a.sport.name]);
    const { getNextAd } = Ads.useContainer();
    const currentPeriod = useMemo(() => getCurrentPeriod(matchSummary), [matchSummary]);
    const timeoutCallback = useCallback(([name, value, graphicProps], index) => {
        var _a, _b;
        if (graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.guard) {
            let blocked;
            const args = {
                currentSequence,
                matchSummary: matchSummary,
                sequenceQueue,
                match,
                clockData,
                desiredState
            };
            if (Array.isArray(graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.guard)) {
                blocked = graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.guard.find((guard) => !guard(args));
            }
            else {
                blocked = !graphicProps.guard(args);
            }
            if (blocked) {
                return dispatch({
                    type: 'IGNORE_GUARDED_TRANSITION',
                    payload: {
                        graphicProps,
                        index,
                        name
                    }
                });
            }
        }
        const isAction = name.slice(0, 7) === 'action:';
        if (isAction) {
            const newState = getActionProps(name.slice(7), graphicProps, currentPeriod, sequenceQueue, desiredState, overlay.enableAds, getNextAd, [...((theme === null || theme === void 0 ? void 0 : theme.graphics) || []), detachedAd]);
            const isLast = index !== currentSequence.transitions.length - 1;
            const action = {
                type: isLast ? 'APPLY_TRANSITION' : 'APPLY_LAST_TRANSITION',
                payload: {
                    index,
                    newState
                }
            };
            return dispatch(action);
        }
        const themeGraphicSizes = ((_a = [...((theme === null || theme === void 0 ? void 0 : theme.graphics) || []), detachedAd].find(({ name: graphicName }) => camelCase(graphicName) === name)) === null || _a === void 0 ? void 0 : _a.imageAdSizes) || [];
        const action = {
            type: index !== currentSequence.transitions.length - 1 ? 'APPLY_TRANSITION' : 'APPLY_LAST_TRANSITION',
            payload: {
                index,
                updateType: graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.updateType,
                newState: {
                    [name]: Object.assign(Object.assign({}, graphicProps), ((graphicProps && overlay.enableAds
                        ? getNextAd(Object.assign(Object.assign({}, graphicProps), { adSizes: (_b = graphicProps === null || graphicProps === void 0 ? void 0 : graphicProps.adSizes) === null || _b === void 0 ? void 0 : _b.filter((size) => themeGraphicSizes.includes(size)) }))
                        : undefined) || []))
                }
            }
        };
        dispatch(action);
    }, [dispatch, currentPeriod, sequenceQueue, currentSequence, matchSummary, clockData, desiredState, match]);
    // @ts-ignore
    timeoutFunction.current = timeoutCallback;
    useEffect(() => {
        const topPrioritySequence = sequenceQueue[sequenceQueue.length - 1];
        if (!topPrioritySequence || (topPrioritySequence.guard && !topPrioritySequence.guard(baseArgs))) {
            return;
        }
        if (!currentSequence) {
            dispatch({ type: 'UPDATE_CURRENT_SEQUENCE', payload: topPrioritySequence });
        }
        else if (topPrioritySequence.priority < currentSequence.priority) {
            dispatch({ type: 'UPDATE_CURRENT_SEQUENCE', payload: topPrioritySequence });
        }
        else if ([2].includes(topPrioritySequence.priority) &&
            topPrioritySequence.priority === currentSequence.priority) {
            dispatch({ type: 'UPDATE_CURRENT_SEQUENCE', payload: topPrioritySequence });
        }
    }, [currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.name, sequenceQueue]);
    useEffect(() => {
        // TODO: add in proper setTimeout type;
        const timeouts = [];
        const transitions = (currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.transitions.slice((currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex) ? (currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex) + 1 : 0)) || [];
        const firstOffset = (currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex) ? transitions[0][1] : 0;
        transitions.forEach(([name, value, graphicProps], index) => {
            timeouts.push(setTimeout(() => {
                if (timeoutFunction.current) {
                    timeoutFunction.current([name, value, graphicProps], index + ((currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex) ? (currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex) + 1 : 0));
                }
            }, Math.max(0, value - firstOffset) * 1000));
        });
        return () => {
            timeouts.map(timeout => clearTimeout(timeout));
        };
    }, [`${currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.name} ${currentSequence === null || currentSequence === void 0 ? void 0 : currentSequence.lastBlockedIndex}`]);
    const baseArgs = {
        currentSequence,
        matchSummary: matchSummary,
        match,
        clockData,
        desiredState
    };
    const enqueueFactSequenceFunction = useCallback(() => {
        if (!newFacts) {
            return;
        }
        // split by new facts
        let newSequences = [];
        let triggers = liveTriggers;
        let triggerNames = Object.keys(liveTriggers[camelCase(match.sportName)]);
        let factSequenceTriggerMap = liveFactSequenceTriggerMap;
        if ((currentPeriod === null || currentPeriod === void 0 ? void 0 : currentPeriod.status) === 'break') {
            triggers = breakTriggers;
            triggerNames = Object.keys(breakTriggers[camelCase(match.sportName)]);
            factSequenceTriggerMap = breakFactSequenceTriggerMap;
        }
        newSequences = triggerNames
            .map(triggerName => {
            const sequences = newFacts.map((fact) => {
                // @ts-ignore
                let isTriggered = triggers[camelCase(match.sportName)][triggerName](Object.assign(Object.assign({}, baseArgs), { fact }));
                let milestoneData;
                if (Array.isArray(isTriggered)) {
                    milestoneData = isTriggered[1];
                    isTriggered = isTriggered[0];
                }
                if (isTriggered) {
                    let sequence = 
                    // @ts-ignore
                    factSequenceTriggerMap[camelCase(match.sportName)][triggerName];
                    if (typeof sequence === 'function') {
                        sequence = sequence(Object.assign(Object.assign({}, baseArgs), { fact }), milestoneData);
                    }
                    sequence.triggeringFactId = fact.id;
                    return sequence;
                }
            });
            return sequences;
        })
            .flat()
            .filter(val => val)
            // @ts-ignore
            .filter(val => !usedFacts[val.triggeringFactId]);
        if (newSequences.length) {
            dispatch({ type: 'ENQUEUE_NEW_SEQUENCES', payload: newSequences, src: 'FACT_CHANGE' });
        }
    }, [currentSequence, matchSummary, match, clockData, desiredState, newFacts]);
    useEffect(enqueueFactSequenceFunction, [newFacts]);
    useEffect(() => {
        if ((currentPeriod === null || currentPeriod === void 0 ? void 0 : currentPeriod.status) === 'break') {
            let sequence = createBreakTimeline[camelCase(match.sportName)](currentPeriod);
            if (typeof sequence === 'function') {
                sequence = sequence(Object.assign({}, baseArgs));
            }
            dispatch({ type: 'ENQUEUE_NEW_SEQUENCES', payload: [sequence], src: 'PERIOD_CHANGE' });
        }
        else if ((currentPeriod === null || currentPeriod === void 0 ? void 0 : currentPeriod.status) === 'live') {
            const sequence = createLiveTimeline[camelCase(match.sportName)](currentPeriod);
            dispatch({ type: 'ENQUEUE_NEW_SEQUENCES', payload: [sequence], src: 'PERIOD_CHANGE' });
        }
    }, [currentPeriod.code]);
    // implement trigger for adLoop based on competition.adAllocation
    const loop = useAdLoop(currentPeriod, (duration, allowedSizes) => dispatch({
        type: 'ENQUEUE_NEW_SEQUENCES',
        payload: adLoopSequence({
            currentSequence,
            match,
            clockData,
            desiredState,
            allowedSizes,
            duration
        }),
        src: 'AD_LOOP'
    }), clockData === null || clockData === void 0 ? void 0 : clockData.secondsSinceStart);
    useEffect(() => {
        const adLoopGraphicBase = Object.values(desiredState).find(({ adLoop }) => adLoop);
        if (adLoopGraphicBase) {
            let allowedSizes = [];
            let loopConfig;
            Object.values(desiredState).find(({ adLoop, adLoopSizes, adLoopConfig, liveLoop }) => {
                var _a, _b, _c, _d;
                if (adLoop) {
                    if (liveLoop && ((_b = (_a = match.competition.adAllocation) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.loop.enabled)) {
                        loopConfig = (_d = (_c = match.competition.adAllocation) === null || _c === void 0 ? void 0 : _c.config) === null || _d === void 0 ? void 0 : _d.loop;
                        allowedSizes = adLoopSizes;
                    }
                    else if (!liveLoop) {
                        loopConfig = adLoopConfig;
                        allowedSizes = adLoopSizes;
                    }
                    return true;
                }
            });
            if (allowedSizes === null || allowedSizes === void 0 ? void 0 : allowedSizes.length) {
                loop.start(loopConfig, [...allowedSizes]);
            }
            else {
                loop.stop();
            }
        }
        else {
            loop.stop();
        }
    }, [desiredState, (_c = (_b = match.competition.adAllocation) === null || _b === void 0 ? void 0 : _b.config) === null || _c === void 0 ? void 0 : _c.loop]);
};
export const Automation = createContainer(useAutomation);
//# sourceMappingURL=automationContainer.js.map